import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";

// Plain-English hint for each status. Long-form so users get real context
// when they tap the pill — not just a repeat of the label. The popover
// pattern was added Feb 2026 so users can dig into "why does this say
// Warning?" without having to guess or navigate anywhere.
const HINTS = {
  safe: {
    short: "On track to cover every bill this month.",
    long: "Your projected end balance stays positive after accounting for every unpaid bill and expected paycheck in this window. Nothing to worry about — just keep updating your balances weekly so it stays accurate.",
  },
  tight: {
    short: "Cutting it close this month — leave some cushion.",
    long: "You'll cover your bills, but with less breathing room than usual. If any bill lands higher than expected or a paycheck is delayed, you could dip negative. Consider reviewing upcoming bills or moving a non-urgent charge if you want more margin.",
  },
  short: {
    short: "Bills may exceed cash this month — update balances and review bills.",
    long: "Right now, your available money plus expected income doesn't cover every unpaid bill in this window. Try one of: (1) confirm your bank balances are up to date, (2) reschedule a non-urgent bill to a later pay period, or (3) confirm upcoming income is landing on time.",
  },
};

const STYLES = {
  safe: { className: "status-safe", label: "Safe", dot: "#4A6148", hintColor: "text-[#3B5B3E]" },
  tight: { className: "status-tight", label: "Tight", dot: "#B87A3D", hintColor: "text-[#8C5D30]" },
  short: { className: "status-short", label: "Warning", dot: "#C45B4F", hintColor: "text-[#8C3A30]" },
};

export default function StatusBadge({ status, size = "md", testId, showHint = false }) {
  const s = STYLES[status] || STYLES.safe;
  const hint = HINTS[status] || HINTS.safe;
  const padding = size === "sm" ? "px-2.5 py-0.5 text-xs" : "px-3.5 py-1 text-sm";
  // The pill is now a button so users can tap to open a plain-English
  // popover explaining what the status means and what to do about it.
  // Wrapped in PopoverTrigger so keyboard + touch both work.
  const pill = (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${s.className} inline-flex items-center gap-2 rounded-full font-medium ${padding} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#4A6148] transition-opacity cursor-pointer`}
          data-testid={testId || `status-badge-${status}`}
          aria-label={`${s.label} status — tap for details`}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
          {s.label}
          <Info size={12} className="opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="w-72 p-4 bg-white border-[#D8CFB9] shadow-lg"
        data-testid={`status-badge-${status}-popover`}
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.dot }} />
          <span className={`text-xs font-semibold uppercase tracking-widest ${s.hintColor}`}>
            {s.label}
          </span>
        </div>
        <p className="text-sm text-[#2C302E] font-medium leading-snug mb-2">
          {hint.short}
        </p>
        <p className="text-xs text-[#59605C] leading-relaxed">
          {hint.long}
        </p>
      </PopoverContent>
    </Popover>
  );
  if (!showHint) return pill;
  // Hint variant — pill + explanatory sub-caption stacked underneath.
  // Kept for the marketing/onboarding surfaces that still opt into the
  // always-visible caption pattern.
  return (
    <div className="flex flex-col items-end gap-1.5 max-w-[220px]">
      {pill}
      <p
        className={`text-xs leading-snug text-right italic ${s.hintColor}`}
        data-testid={`${testId || `status-badge-${status}`}-hint`}
      >
        {hint.short}
      </p>
    </div>
  );
}
